/**
 * SessionLeftColumn — left column of AddSessionModal containing date, manpower,
 * product select, product info, and packing materials display.
 * Extracted from AddSessionModal.tsx for readability.
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { type PackingMaterialForSession } from '../../../../services/packingMaterialMappingService';
import productionService from '../../../../services/productionService';
import { Users, Loader2, Layers, Package, AlertTriangle, Calendar, Gauge } from 'lucide-react';
import type { Product, BoxMaterialEntry, InputMode, NewScheduleRow, SelectedLabourFactorMaterial } from './types';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import type { SearchableDropdownOption } from '../../../../components/SearchableDropdown';
import DatePickerDropdown from '../../../../components/DatePickerDropdown';

/** Date input that displays "Monday — 04/06/2026" with a styled dropdown calendar */
function DatePickerField({ label, value, min, max, onChange }: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (val: string) => void;
}) {
  const d = new Date(value + 'T00:00:00');
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
  const formatted = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

  return (
    <DatePickerDropdown selected={value} onSelect={onChange} min={min} max={max}>
      {({ onClick }) => (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
          <button
            type="button"
            onClick={onClick}
            className="w-full px-2 py-1.5 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 cursor-pointer flex items-center justify-between"
          >
            <span>{dayName} — {formatted}</span>
            <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
          </button>
        </div>
      )}
    </DatePickerDropdown>
  );
}

interface PackingMaterialDisplay extends PackingMaterialForSession {
  piecesNeeded: number;
  transferUnitsNeeded: number;
  hasEnoughStock: boolean;
}

interface ManpowerSummary {
  summary: {
    operators: number;
    packers: number;
    stations: number;
  };
  operators?: Array<{ full_name?: string; employee_number: string }>;
  packers?: Array<{ full_name?: string; employee_number: string }>;
  stations?: Array<{ station_name: string }>;
}

interface SessionLeftColumnProps {
  newRow: NewScheduleRow;
  setNewRow: React.Dispatch<React.SetStateAction<NewScheduleRow>>;
  onTransferDateChange: (value: string) => void;
  products: Product[];
  selectedProduct: Product | undefined;
  inputMode: InputMode;
  boxEntries: BoxMaterialEntry[];
  updateBoxMaterial: (index: number, field: string, value: string) => void;
  boxTotalBoxes: number;
  handleProductChange: (sku: string) => void;
  manpowerData: ManpowerSummary | undefined;
  loadingManpower: boolean;
  packingMaterialsDisplay: PackingMaterialDisplay[];
  selectedLabourFactorMaterials: SelectedLabourFactorMaterial[];
}

const SessionLeftColumn: React.FC<SessionLeftColumnProps> = ({
  newRow,
  setNewRow,
  onTransferDateChange,
  products,
  selectedProduct,
  inputMode,
  boxEntries,
  updateBoxMaterial,
  boxTotalBoxes,
  handleProductChange,
  manpowerData,
  loadingManpower,
  packingMaterialsDisplay,
  selectedLabourFactorMaterials,
}) => {
  const { t } = useLanguage();

  // Labour estimator parameters — fixed defaults, not user-editable
  const estimatorWorkers = 2;
  const estimatorSessions = 1;
  const estimatorHours = 8;

  // The LF standards are OUTPUT lb/hr for a 2-worker crew (WCO-2474) — the value already accounts
  // for the crew AND the yield. So capacity = (standard ÷ crew) × worker-hours, with NO extra
  // × yield. Mirrors @tnb/labour-factor STANDARD_CREW_SIZE (kept local — the frontend can't import
  // the Node shared package).
  const STANDARD_CREW_SIZE = 2;

  // Block past dates on both date pickers
  const today = new Date().toLocaleDateString('en-CA');

  // Async search state for unlinked products
  const [asyncProducts, setAsyncProducts] = useState<Product[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search for all HTE finished products (including unlinked)
  const handleProductSearch = useCallback((text: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 2) {
      setAsyncProducts([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await productionService.searchAllProducts(text.trim());
        setAsyncProducts(results);
      } catch {
        setAsyncProducts([]);
      }
    }, 300);
  }, []);

  // Merge linked products (default) with async search results (unlinked only)
  const productOptions = useMemo(() => {
    const linkedSkus = new Set(products.map(p => p.sku));
    const opts: SearchableDropdownOption[] = products.map(p => ({
      value: p.sku,
      label: `${p.sku} - ${p.name}${p.item_chinese_name ? ` / ${p.item_chinese_name}` : ''}`,
    }));

    // Add unlinked products from async search results
    const unlinked = asyncProducts.filter(p => !linkedSkus.has(p.sku));
    if (unlinked.length > 0) {
      opts.push({ value: '', label: t('supervisor.otherProducts') || 'Other Products (no materials linked)', isGroupHeader: true });
      unlinked.forEach(p => {
        opts.push({
          value: p.sku,
          label: `${p.sku} - ${p.name}${p.item_chinese_name ? ` / ${p.item_chinese_name}` : ''} [${t('supervisor.noMaterials') || 'NEW'}]`,
        });
      });
    }

    return opts;
  }, [products, asyncProducts, t]);

  const [expandedManpower, setExpandedManpower] = useState<'operators' | 'packers' | 'stations' | null>(null);
  const [expandedPackingItem, setExpandedPackingItem] = useState<string | null>(null);

  return (
    <div className="space-y-3 overflow-y-auto pr-1">
      {/* Production Date & Transfer Date side by side */}
      <div className="grid grid-cols-2 gap-3">
        <DatePickerField
          label={t('supervisor.productionDate')}
          value={newRow.scheduled_date}
          min={today}
          onChange={(v) => setNewRow(prev => ({ ...prev, scheduled_date: v }))}
        />
        <DatePickerField
          label={t('supervisor.transferDate')}
          value={newRow.transfer_date}
          min={today}
          max={newRow.scheduled_date}
          onChange={onTransferDateChange}
        />
      </div>
      {/* Weekend warnings */}
      {(() => {
        const warnings: string[] = [];
        const prodD = new Date(newRow.scheduled_date + 'T00:00:00');
        const prodDay = prodD.getDay();
        if (prodDay === 0 || prodDay === 6) {
          warnings.push(t('supervisor.weekendWarningProduction').replace('{{day}}', prodDay === 6 ? 'Saturday' : 'Sunday'));
        }
        const transD = new Date(newRow.transfer_date + 'T00:00:00');
        const transDay = transD.getDay();
        if (transDay === 0 || transDay === 6) {
          warnings.push(t('supervisor.weekendWarningTransfer').replace('{{day}}', transDay === 6 ? 'Saturday' : 'Sunday'));
        }
        if (warnings.length === 0) return null;
        return (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm font-medium text-amber-700 dark:text-amber-300">
              {warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          </div>
        );
      })()}

      {/* Manpower Summary */}
      <div className={`rounded-lg p-3 ${
        !loadingManpower && manpowerData?.summary && (manpowerData.summary.operators > 0 || manpowerData.summary.packers > 0)
          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
          : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('supervisor.manpowerForDate') || 'Scheduled Manpower'}
          </span>
          {loadingManpower && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
        </div>
        {!loadingManpower && manpowerData?.summary ? (
          <>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <button
                type="button"
                onClick={() => setExpandedManpower(expandedManpower === 'operators' ? null : 'operators')}
                className={`flex items-center gap-2 p-1 -m-1 rounded-lg transition-colors ${
                  manpowerData.summary.operators > 0 ? 'hover:bg-green-100 dark:hover:bg-green-900/40 cursor-pointer' : ''
                }`}
                disabled={manpowerData.summary.operators === 0}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  manpowerData.summary.operators > 0
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                }`}>
                  <span className="font-bold">{manpowerData.summary.operators}</span>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.operators') || 'Operators'}</span>
              </button>
              <button
                type="button"
                onClick={() => setExpandedManpower(expandedManpower === 'packers' ? null : 'packers')}
                className={`flex items-center gap-2 p-1 -m-1 rounded-lg transition-colors ${
                  manpowerData.summary.packers > 0 ? 'hover:bg-purple-100 dark:hover:bg-purple-900/40 cursor-pointer' : ''
                }`}
                disabled={manpowerData.summary.packers === 0}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  manpowerData.summary.packers > 0
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                }`}>
                  <span className="font-bold">{manpowerData.summary.packers}</span>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('supervisor.packers') || 'Packers'}</span>
              </button>
              <button
                type="button"
                onClick={() => setExpandedManpower(expandedManpower === 'stations' ? null : 'stations')}
                className={`flex items-center gap-2 p-1 -m-1 rounded-lg transition-colors ${
                  manpowerData.summary.stations > 0 ? 'hover:bg-indigo-100 dark:hover:bg-indigo-900/40 cursor-pointer' : ''
                }`}
                disabled={manpowerData.summary.stations === 0}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  manpowerData.summary.stations > 0
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                }`}>
                  <Layers className="w-4 h-4" />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {manpowerData.summary.stations} {t('supervisor.stations') || 'Stations'}
                </span>
              </button>
            </div>
            {/* Expanded details */}
            {expandedManpower === 'operators' && manpowerData.operators?.length && manpowerData.operators.length > 0 && (
              <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800">
                <div className="flex flex-wrap gap-1.5">
                  {manpowerData.operators.map((op, idx) => (
                    <span key={idx} className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                      {op.full_name || op.employee_number}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {expandedManpower === 'packers' && manpowerData.packers?.length && manpowerData.packers.length > 0 && (
              <div className="mt-2 pt-2 border-t border-purple-200 dark:border-purple-800">
                <div className="flex flex-wrap gap-1.5">
                  {manpowerData.packers.map((pk, idx) => (
                    <span key={idx} className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                      {pk.full_name || pk.employee_number}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {expandedManpower === 'stations' && manpowerData.stations?.length && manpowerData.stations.length > 0 && (
              <div className="mt-2 pt-2 border-t border-indigo-200 dark:border-indigo-800">
                <div className="flex flex-wrap gap-1.5">
                  {manpowerData.stations.map((st, idx) => (
                    <span key={idx} className="px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full">
                      {st.station_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : !loadingManpower ? (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            {t('supervisor.noManpowerScheduled') || 'No manpower scheduled for this date'}
          </p>
        ) : null}
        {!loadingManpower && manpowerData?.summary && manpowerData.summary.operators === 0 && manpowerData.summary.packers === 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            {t('supervisor.noManpowerWarning') || 'Awaiting supervisor to schedule operators'}
          </p>
        )}
      </div>

      {/* Finished Product */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('supervisor.scheduleItemNumber')}
        </label>
        <SearchableDropdown
          options={productOptions}
          value={newRow.product_sku}
          onChange={handleProductChange}
          onSearchChange={handleProductSearch}
          placeholder={`${t('common.select')}...`}
          inputClassName="px-2 py-1.5 text-sm"
        />
      </div>

      {/* Product Info */}
      {selectedProduct && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
          <div className={`grid gap-2 text-sm ${inputMode === 'boxes' ? 'grid-cols-4' : 'grid-cols-2'}`}>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.packing')}:</span>
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedProduct.packing_display || selectedProduct.target_weight || '-'}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.unitWeight') || 'Unit Weight'}:</span>
              <p className="font-medium text-gray-900 dark:text-white">{selectedProduct.unit_weight ? `${Number(selectedProduct.unit_weight).toFixed(2)} lbs` : '-'}</p>
            </div>
            {inputMode === 'boxes' && (
              boxEntries.length === 1 ? (
                <>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.targetCases') || 'Target Cases'}:</span>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      placeholder="0"
                      value={boxEntries[0].target_boxes}
                      onChange={(e) => updateBoxMaterial(0, 'target_boxes', e.target.value)}
                      className="mt-0.5 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
                    />
                  </div>
                  <div className="relative group">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.totalWeight') || 'Total Weight'}:</span>
                    <p className="font-medium text-green-600 dark:text-green-400 underline decoration-dotted">
                      {parseInt(boxEntries[0].target_boxes) > 0 && selectedProduct.unit_weight
                        ? `${(parseInt(boxEntries[0].target_boxes) * selectedProduct.unit_weight).toFixed(2)} lbs`
                        : '-'}
                    </p>
                    {parseInt(boxEntries[0].target_boxes) > 0 && selectedProduct.unit_weight && (
                      <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block z-50">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                          <p>{boxEntries[0].target_boxes} boxes × {Number(selectedProduct.unit_weight).toFixed(2)} lbs = {(parseInt(boxEntries[0].target_boxes) * selectedProduct.unit_weight).toFixed(2)} lbs</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.totalCases') || 'Total Cases'}:</span>
                    <p className="font-medium text-indigo-600 dark:text-indigo-400">{boxTotalBoxes || 0}</p>
                  </div>
                  <div className="relative group">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.totalWeight') || 'Total Weight'}:</span>
                    <p className="font-medium text-green-600 dark:text-green-400 underline decoration-dotted">
                      {boxTotalBoxes > 0 && selectedProduct.unit_weight
                        ? `${(boxTotalBoxes * selectedProduct.unit_weight).toFixed(2)} lbs`
                        : '-'}
                    </p>
                    {boxTotalBoxes > 0 && selectedProduct.unit_weight && (
                      <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block z-50">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                          <p>{boxTotalBoxes} boxes × {Number(selectedProduct.unit_weight).toFixed(2)} lbs = {(boxTotalBoxes * selectedProduct.unit_weight).toFixed(2)} lbs</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      )}

      {/* Labour Factor Output Estimator */}
      {newRow.product_sku && (
        <div className="rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Gauge className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('supervisor.laborFactor')}
            </span>
          </div>

          {selectedLabourFactorMaterials.length > 0 ? (
            <div className="space-y-3">
              {selectedLabourFactorMaterials.map((material) => {
                const hasLF = material.labour_factor != null && material.labour_factor > 0;
                const isHigherLabour = hasLF && material.labour_factor! > 1;
                const isLowerLabour = hasLF && material.labour_factor! < 1;
                const lfTone = !hasLF
                  ? 'text-gray-400 dark:text-gray-500'
                  : isHigherLabour
                    ? 'text-amber-700 dark:text-amber-400'
                    : isLowerLabour
                      ? 'text-teal-600 dark:text-teal-400'
                      : 'text-gray-900 dark:text-white';

                // Planning calculation
                const workerHours = estimatorWorkers * estimatorHours * estimatorSessions;
                const yieldPotential = material.input_weight * material.yield_factor;

                let estimatedOutput: number | null = null;
                let limitingFactor: 'yield' | 'labour' | null = null;

                if (hasLF && material.baseline_throughput && workerHours > 0) {
                  const materialThroughput = material.baseline_throughput / material.labour_factor!;
                  // materialThroughput = the material's standard OUTPUT lb/hr for a 2-worker crew.
                  // Convert to per-worker-hour (÷ crew) then scale by worker-hours → total OUTPUT lbs
                  // the labour can produce. The standard already bakes in yield → NO extra × yield.
                  const labourCapacity = (materialThroughput / STANDARD_CREW_SIZE) * workerHours;
                  estimatedOutput = Math.min(yieldPotential, labourCapacity);
                  limitingFactor = labourCapacity < yieldPotential ? 'labour' : 'yield';
                } else if (material.input_weight > 0) {
                  // Yield-only fallback when no LF
                  estimatedOutput = yieldPotential;
                }

                return (
                  <div key={material.item_number} className="space-y-2">
                    {/* Material header + LF badge */}
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{material.item_number}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{material.item_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        {hasLF ? (
                          <p className={`font-semibold ${lfTone}`}>{material.labour_factor!.toFixed(2)}</p>
                        ) : (
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                            {t('supervisor.labourNoFactor')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Fixed parameters (read-only defaults) */}
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { label: t('supervisor.labourWorkers'), value: estimatorWorkers },
                        { label: t('supervisor.labourSessions'), value: estimatorSessions },
                        { label: t('supervisor.labourDuration'), value: estimatorHours },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex flex-col items-center gap-0.5">
                          <label className="text-[10px] text-gray-500 dark:text-gray-400 text-center leading-tight">{label}</label>
                          <input
                            type="number"
                            value={value}
                            readOnly
                            className="w-full text-center text-sm font-medium border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white py-0.5 cursor-default focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Max output for the day — labour-driven, independent of entered input weight */}
                    {hasLF && material.baseline_throughput && workerHours > 0 && (() => {
                      // Standard is OUTPUT lb/hr for a 2-worker crew (already includes yield):
                      // ÷ crew → per-worker-hour, × worker-hours → total OUTPUT lbs. No extra × yield.
                      const maxOutput = ((material.baseline_throughput / material.labour_factor!) / STANDARD_CREW_SIZE) * workerHours;
                      const unitWeight = selectedProduct?.unit_weight || 0;
                      const maxCases = unitWeight > 0 ? Math.floor(maxOutput / unitWeight) : null;
                      const boxIndex = boxEntries.findIndex(b => b.item_number === material.item_number);
                      // WCO-2544: allowance derates the labour-driven max — MULTIPLY the result by
                      // (1 − pct/100). When applying to Target Cases, prefer the allowance-adjusted
                      // cases (the realistic plan) if an allowance is set.
                      const allowancePct = material.allowance_pct || 0;
                      const hasAllowance = allowancePct > 0;
                      const allowanceMaxOutput = hasAllowance ? maxOutput * (1 - allowancePct / 100) : null;
                      const allowanceMaxCases = (hasAllowance && unitWeight > 0 && allowanceMaxOutput != null)
                        ? Math.floor(allowanceMaxOutput / unitWeight) : null;
                      const applyCases = hasAllowance ? allowanceMaxCases : maxCases;
                      const canApply = applyCases !== null && boxIndex >= 0;
                      return (
                        <div className="rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 px-2 py-1.5 space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-300">{t('supervisor.labourMaxForDay')}</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                                {maxOutput.toFixed(2)} lb
                              </span>
                              {maxCases !== null && (
                                (canApply && !hasAllowance) ? (
                                  <button
                                    type="button"
                                    onClick={() => updateBoxMaterial(boxIndex, 'target_boxes', String(maxCases))}
                                    title={t('supervisor.labourMaxApplyHint') || 'Click to set Target Cases'}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-700/50 transition-colors cursor-pointer"
                                  >
                                    {t('supervisor.labourEstimatedOutputCases').replace('{{count}}', String(maxCases))}
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                    {t('supervisor.labourEstimatedOutputCases').replace('{{count}}', String(maxCases))}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                          {hasAllowance && allowanceMaxOutput != null && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-500 dark:text-gray-400">
                                {t('supervisor.labourWithAllowance').replace('{{pct}}', String(allowancePct))}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-emerald-600 dark:text-emerald-300">
                                  {allowanceMaxOutput.toFixed(2)} lb
                                </span>
                                {allowanceMaxCases !== null && (
                                  canApply ? (
                                    <button
                                      type="button"
                                      onClick={() => updateBoxMaterial(boxIndex, 'target_boxes', String(allowanceMaxCases))}
                                      title={t('supervisor.labourMaxApplyHint') || 'Click to set Target Cases'}
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-700/50 transition-colors cursor-pointer"
                                    >
                                      {t('supervisor.labourEstimatedOutputCases').replace('{{count}}', String(allowanceMaxCases))}
                                    </button>
                                  ) : (
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                      {t('supervisor.labourEstimatedOutputCases').replace('{{count}}', String(allowanceMaxCases))}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Estimation results */}
                    {material.input_weight > 0 && estimatedOutput !== null && (() => {
                      const unitWeight = selectedProduct?.unit_weight || 0;
                      const estimatedCases = unitWeight > 0 ? Math.floor(estimatedOutput / unitWeight) : null;
                      return (
                      <div className="rounded bg-slate-100 dark:bg-slate-700/50 px-2 py-1.5 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500 dark:text-gray-400">{t('supervisor.labourEstimatedOutput')}</span>
                          <div className="text-right">
                            <span className="font-semibold text-orange-600 dark:text-orange-400">
                              {estimatedOutput.toFixed(2)} lb
                            </span>
                            {estimatedCases !== null && (
                              <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">
                                ({t('supervisor.labourEstimatedOutputCases').replace('{{count}}', String(estimatedCases))})
                              </span>
                            )}
                            {!hasLF && (
                              <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">
                                ({t('supervisor.labourYieldOnlyEstimate')})
                              </span>
                            )}
                          </div>
                        </div>
                        {hasLF && limitingFactor && (
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-right">
                            {t('supervisor.labourBasedOnInput').replace('{{weight}}', material.input_weight.toFixed(2))}
                            {' · '}{limitingFactor === 'labour' ? '⚡ labour-limited' : '⚖️ yield-limited'}
                          </p>
                        )}
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {t('supervisor.selectMaterialForLaborFactor')}
            </p>
          )}
        </div>
      )}

      {/* Packing Materials */}
      {packingMaterialsDisplay.length > 0 && (
        <div className="mt-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Package className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {t('packingMaterialMapping.packingMaterials')}
            </span>
          </div>
          <div className="space-y-1">
            {packingMaterialsDisplay.map((mat) => {
              const hasBoxes = mat.transferUnitsNeeded > 0;
              const isExpanded = expandedPackingItem === mat.packing_item_number;
              const availablePieces = Number(mat.available_pieces) || 0;
              const availableUnits = Number(mat.available_units) || 0;
              return (
                <div
                  key={mat.packing_item_number}
                  onClick={() => setExpandedPackingItem(isExpanded ? null : mat.packing_item_number)}
                  className="px-2 py-1.5 bg-gray-50 dark:bg-gray-700/50 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors"
                >
                  {/* Row 1: Name + badge */}
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      !hasBoxes ? 'bg-gray-300 dark:bg-gray-600'
                        : mat.hasEnoughStock ? 'bg-green-500' : 'bg-amber-500'
                    }`} />
                    <span className={`text-xs text-gray-900 dark:text-white ${isExpanded ? '' : 'truncate'}`}>
                      {mat.packing_name || mat.packing_item_number}
                    </span>
                    <span className={`text-[10px] leading-tight px-1 py-0.5 rounded flex-shrink-0 ${
                      mat.packing_level === 'subunit'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    }`}>
                      {mat.packing_level === 'subunit'
                        ? t('packingMaterialMapping.level_subunit')
                        : t('packingMaterialMapping.level_case')}
                    </span>
                  </div>
                  {/* Row 2: Stats */}
                  <div className="flex items-center gap-3 mt-0.5 text-[11px]">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('packingMaterialMapping.available')}: <span className="font-medium text-gray-700 dark:text-gray-300">{availablePieces} pc ({availableUnits}{mat.item_unit ? ` ${mat.item_unit}` : ''})</span>
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('packingMaterialMapping.qtyNeeded')}: <span className={`font-medium ${
                        !hasBoxes ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'
                      }`}>{hasBoxes ? `${mat.piecesNeeded} pc` : '—'}</span>
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('packingMaterialMapping.qtyToTransfer')}: <span className={`font-medium ${
                        !hasBoxes ? 'text-gray-400 dark:text-gray-500'
                          : mat.hasEnoughStock ? 'text-gray-700 dark:text-gray-300' : 'text-amber-600 dark:text-amber-400'
                      }`}>{hasBoxes ? `${mat.transferUnitsNeeded}${mat.item_unit ? ` ${mat.item_unit}` : ''}` : '—'}</span>
                    </span>
                  </div>
                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-600 text-[11px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{mat.packing_item_number}</span>
                      {mat.packing_chinese_name && <span>{mat.packing_chinese_name}</span>}
                      {mat.packing_unit_weight > 0 && <span>{t('supervisor.unitWeight')}: {Number(mat.packing_unit_weight).toFixed(2)} lbs</span>}
                      {mat.packing_info && <span>{t('supervisor.packing')}: {mat.packing_info}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionLeftColumn;
